import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchSettings, updateSettings } from '@/api';

// Common timezones for easy selection
const COMMON_TIMEZONES = [
  { value: 'Africa/Windhoek', label: 'Central Africa Time (CAT) - Windhoek' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time (SAST)' },
  { value: 'Africa/Lagos', label: 'West Africa Time (WAT) - Lagos' },
  { value: 'Africa/Nairobi', label: 'East Africa Time (EAT) - Nairobi' },
  { value: 'Africa/Cairo', label: 'Egypt Standard Time - Cairo' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT) - London' },
  { value: 'Europe/Paris', label: 'Central European Time (CET) - Paris' },
  { value: 'America/New_York', label: 'Eastern Time (ET) - New York' },
  { value: 'America/Chicago', label: 'Central Time (CT) - Chicago' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - Denver' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST) - Dubai' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST) - Kolkata' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST) - Tokyo' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET) - Sydney' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (NZST)' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
];

const generalSettingsSchema = z.object({
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone must be at least 10 characters'),
  address: z.string().min(5, 'Address must be at least 5 characters'),
  vat_percentage: z.string()
    .transform(val => parseFloat(val))
    .refine(val => !isNaN(val) && val >= 0 && val <= 100, {
      message: 'VAT percentage must be a number between 0 and 100',
    }),
  currency_symbol: z.string().min(1, 'Currency symbol is required').max(10, 'Currency symbol must be 10 characters or less'),
  profit_margin: z.string()
    .transform(val => parseFloat(val))
    .refine(val => !isNaN(val) && val >= 0 && val <= 100, {
      message: 'Profit margin must be a number between 0 and 100',
    }),
  damage_percentage: z.string()
    .transform(val => parseFloat(val))
    .refine(val => !isNaN(val) && val >= 0 && val <= 100, {
      message: 'Damage percentage must be a number between 0 and 100',
    }),
  price_adjustment_limit: z.string()
    .transform(val => parseFloat(val))
    .refine(val => !isNaN(val) && val >= 0 && val <= 500, {
      message: 'Price adjustment limit must be a number between 0 and 500',
    }),
  timezone: z.string().min(1, 'Timezone is required'),
});

type GeneralSettingsData = z.infer<typeof generalSettingsSchema>;

export function SettingsForm() {
  const [loading, setLoading] = useState(true);
  const generalForm = useForm<GeneralSettingsData>({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues: {
      company_name: '',
      email: '',
      phone: '',
      address: '',
      vat_percentage: 0,
      currency_symbol: '$',
      profit_margin: 10,
      damage_percentage: 0,
      price_adjustment_limit: 500,
      timezone: 'Africa/Windhoek',
    },
  });

  // Load current settings on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchSettings();
        generalForm.reset({
          company_name: res.data.company_name || '',
          email: res.data.email || '',
          phone: res.data.phone || '',
          address: res.data.address || '',
          vat_percentage: res.data.vat_percentage !== undefined ? res.data.vat_percentage : 0,
          currency_symbol: res.data.currency_symbol || '$',
          profit_margin: res.data.profit_margin !== undefined ? res.data.profit_margin : 10,
          damage_percentage: res.data.damage_percentage !== undefined ? res.data.damage_percentage : 0,
          price_adjustment_limit: res.data.price_adjustment_limit !== undefined ? res.data.price_adjustment_limit : 500,
          timezone: res.data.timezone || 'Africa/Windhoek',
        });
      } catch (err: unknown) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [generalForm]);

  const onGeneralSubmit = async (data: GeneralSettingsData) => {
    try {
      const res = await updateSettings(data);
      generalForm.reset({
        company_name: res.data.company_name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
        vat_percentage: res.data.vat_percentage !== undefined ? res.data.vat_percentage : 0,
        currency_symbol: res.data.currency_symbol || '$',
        profit_margin: res.data.profit_margin !== undefined ? res.data.profit_margin : 10,
        damage_percentage: res.data.damage_percentage !== undefined ? res.data.damage_percentage : 0,
        price_adjustment_limit: res.data.price_adjustment_limit !== undefined ? res.data.price_adjustment_limit : 500,
        timezone: res.data.timezone || 'Africa/Windhoek',
      });
      toast.success('Settings updated successfully');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Update failed';
      toast.error(errorMessage);
    }
  };

  return (
    <Tabs defaultValue="general" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="general">General</TabsTrigger>
      </TabsList>
      <TabsContent value="general">
        <Form {...generalForm}>
          <form onSubmit={generalForm.handleSubmit(onGeneralSubmit)} className="space-y-6">
            <FormField
              control={generalForm.control}
              name="company_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input disabled={loading} {...field} />
                  </FormControl>
                  <FormDescription>
                    This name appears on invoices and documents.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input disabled={loading} {...field} />
                  </FormControl>
                  <FormDescription>
                    Primary contact email for notifications.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input disabled={loading} {...field} />
                  </FormControl>
                  <FormDescription>
                    Main business contact number.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Address</FormLabel>
                  <FormControl>
                    <Input disabled={loading} {...field} />
                  </FormControl>
                  <FormDescription>
                    Primary address for your business.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="vat_percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>VAT Percentage (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      disabled={loading}
                      {...field}
                      value={field.value.toString()}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    The VAT percentage to apply to sales (e.g., 15 for 15%).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="currency_symbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Currency Symbol</FormLabel>
                  <FormControl>
                    <Input
                      disabled={loading}
                      {...field}
                      placeholder="e.g., $, €, £"
                    />
                  </FormControl>
                  <FormDescription>
                    The currency symbol to use throughout the system (e.g., $ for USD).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="profit_margin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Profit Margin (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      disabled={loading}
                      {...field}
                      value={field.value.toString()}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    The default profit margin percentage for pricing (e.g., 10 for 10%).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="price_adjustment_limit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>POS Price Adjustment Limit (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="500"
                      disabled={loading}
                      {...field}
                      value={field.value.toString()}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    Maximum percentage above or below the catalog price that staff can charge at the register.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="damage_percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Damage Percentage (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      disabled={loading}
                      {...field}
                      value={field.value.toString()}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    The percentage used to calculate damage allowance for inventory (e.g., 5 for 5%).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={generalForm.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>System Timezone</FormLabel>
                  <Select
                    disabled={loading}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a timezone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COMMON_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The timezone used for all date and time operations in the system. Select your local timezone to ensure sales and layaways are recorded with the correct date.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Loading...' : 'Save Changes'}
            </Button>
          </form>
        </Form>
      </TabsContent>
    </Tabs>
  );
}
